import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Spinner } from '@iwoca/orion';

import { MoneyNoDecimals } from '../../../ui/moneyNoDecimals/MoneyNoDecimals';
import EstimatedRepayments from './EstimatedRepayments';
import messages from './CustomiseOffer.messages';
import styles from './CustomiseOffer.module.css';
import { useForm } from 'react-hook-form';
import { intlFormatCurrency } from '@iwIntlProvider/FormattedCurrency';
import WithdrawalSummary from './withdrawalSummary';
import TipBox from './components/TipBox';
import LoadingCalculationValue from './components/LoadingCalculationValue';
import {
    LapiError,
    fetchGetMcaEstimatedSchedule,
    fetchPutFormalOffersMerchantCashAdvanceConfiguration
} from '@iwoca/lapi-client/edge';
import { OPEN_ERROR_MODAL } from '@iwModals/Modals.actions';
import { useQuery } from '@tanstack/react-query';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { useEffect, useMemo, useState } from 'react';
import { AmountSelect } from '../../../ui/amountSelect/amountSelect';
import { Dispatch } from 'redux';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { buildLocationWithSearch } from '../../../drawDown/DrawDownHelper';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { SweepSelect } from '../../../ui/sweepSelect/sweepSelect';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    startDrawdown: ({
        amount,
        sweep,
        offerId
    }: {
        amount: number;
        sweep: number;
        offerId: string;
    }) => {
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            //@ts-ignore
            location: buildLocationWithSearch({
                pathname: '/funding-requirements/',
                offerId: offerId,
                amount: amount,
                sweep: sweep
            })
        });
    },
    openErrorModal: () =>
        dispatch({
            type: OPEN_ERROR_MODAL,
            id: 'FallbackErrorModal'
        })
});

type RblForm = {
    amount: number;
    sweep: number;
};

type TCustomiseOffer = ReturnType<typeof mapDispatchToProps>;

const CustomiseOffer = ({ startDrawdown, openErrorModal }: TCustomiseOffer) => {
    const intl = useIntl();
    const stateKey = useStateKey();
    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors }
    } = useForm<RblForm>();

    const amount = watch('amount');
    const sweep = watch('sweep');

    const [sweepVals, setSweepVals] = useState({
        absMin: 0,
        absMax: 0,
        min: 0,
        max: 0
    });
    const [amountVals, setAmountVals] = useState({
        min: 0,
        max: 0
    });

    const convertSweepToPercentage = (sweep: number) => {
        return Math.round(sweep * 10000) / 100;
    };
    const convertSweepToDecimal = (sweep: number) => {
        return Math.round(sweep * 100) / 10000;
    };

    // Data Fetching
    const { data: accounts, isError: accountsError } = useQuery(
        getAccountsQueryConfig(stateKey)
    );
    const offer_id = useMemo(
        () =>
            accounts?.data?.accounts.find(
                (account) =>
                    (account?.latest_formal_offer?.available_credit || 0) > 0
            )?.latest_formal_offer?.offer_id,
        [accounts]
    );

    const {
        data: schedule,
        isFetching: scheduleLoading,
        error: scheduleError,
        isInitialLoading
    } = useQuery({
        queryKey: ['mcaSchedule', stateKey, offer_id, amount, sweep],
        queryFn: () => {
            return fetchGetMcaEstimatedSchedule({
                stateKey: stateKey,
                offerId: offer_id!,
                query: {
                    // @ts-expect-error
                    cash_amount: amount ? amount : undefined,
                    sweep_fraction: sweep
                        ? convertSweepToDecimal(sweep)
                        : undefined
                }
            });
        },
        enabled: Boolean(offer_id),
        keepPreviousData: true
    });

    // Set intial amount values
    if (!amount && schedule) {
        const initialAmount = Math.floor(
            schedule.data.summary.amount.maximum_transaction_amount
        );
        setValue('amount', initialAmount);
        setAmountVals({
            min: Math.ceil(
                schedule.data.summary.amount.minimum_transaction_amount
            ),
            max: initialAmount
        });
    }

    // Set intial sweep values
    if (!sweep && schedule) {
        const initialSweepMin = convertSweepToPercentage(
            schedule.data.summary.sweep.minimum_sweep_fraction
        );
        const initialSweepMax = convertSweepToPercentage(
            schedule.data.summary.sweep.maximum_sweep_fraction
        );

        setValue('sweep', initialSweepMax);
        setSweepVals({
            absMin: 0.1,
            absMax: initialSweepMax,
            min: initialSweepMin,
            max: initialSweepMax
        });
    }

    useEffect(() => {
        const checkSweepInRange = (sweep: number, min: number, max: number) => {
            if (!sweep || !(sweep >= min && sweep <= max)) {
                setValue('sweep', max);
            }
        };

        if (schedule) {
            const newSweepMin = convertSweepToPercentage(
                schedule.data.summary.sweep.minimum_sweep_fraction
            );
            const newSweepMax = convertSweepToPercentage(
                schedule.data.summary.sweep.maximum_sweep_fraction
            );
            setSweepVals((prevSweepVals) => ({
                ...prevSweepVals,
                min: newSweepMin,
                max: newSweepMax
            }));

            checkSweepInRange(sweep, newSweepMin, newSweepMax);

            setAmountVals({
                min: Math.ceil(
                    schedule.data.summary.amount.minimum_transaction_amount
                ),
                max: Math.floor(
                    schedule.data.summary.amount.maximum_transaction_amount
                )
            });
        }
    }, [schedule, setSweepVals, sweep, setValue]);

    // API Errors
    if (
        accountsError ||
        (scheduleError && (scheduleError as LapiError)?.statusCode !== 400)
    ) {
        openErrorModal();
    }

    if (isInitialLoading) {
        return (
            <div
                className={styles.spinner}
                data-testid="rblOfferLoadingSpinner"
            >
                <Spinner size="large" />
            </div>
        );
    }

    const amountErrorMessage = intl.formatMessage(
        messages.withdrawalAmountError,
        {
            minAmount: intlFormatCurrency({
                intl: intl,
                value: amountVals.min
            }),
            maxAmount: intlFormatCurrency({
                intl: intl,
                value: amountVals.max
            })
        }
    );
    const sweepErrorMessage = intl.formatMessage(messages.drawdownSweepError, {
        minSweep: convertSweepToPercentage(sweepVals.min),
        maxSweep: convertSweepToPercentage(sweepVals.max)
    });
    return (
        <form
            className={styles.approvedForm}
            onSubmit={handleSubmit(async (data) => {
                if (offer_id) {
                    try {
                        await fetchPutFormalOffersMerchantCashAdvanceConfiguration(
                            {
                                stateKey,
                                offerId: offer_id,
                                body: {
                                    data: {
                                        cash_amount: amount,
                                        revenue_sweep_fraction:
                                            convertSweepToDecimal(sweep)
                                    }
                                }
                            }
                        );
                        void startDrawdown({
                            amount: data.amount,
                            sweep: convertSweepToDecimal(data.sweep),
                            offerId: offer_id
                        });
                    } catch (err) {
                        openErrorModal();
                    }
                }
            })}
        >
            <AmountSelect
                {...register('amount', {
                    min: {
                        value: amountVals.min,
                        message: amountErrorMessage
                    },
                    max: {
                        value: amountVals.max,
                        message: amountErrorMessage
                    }
                })}
                className={styles.drawdown}
                label={messages.withdrawalAmountHeader}
                defaultAmount={amount}
                debounce={500}
                onChange={(value) =>
                    setValue('amount', value, { shouldValidate: true })
                }
                slider={{
                    min: amountVals.min,
                    max: amountVals.max
                }}
                error={errors.amount?.message}
            />

            <div className={styles.sweep}>
                <SweepSelect
                    label={messages.drawdownSweepHeader}
                    {...register('sweep', {
                        min: {
                            value: sweepVals.min,
                            message: sweepErrorMessage
                        },
                        max: {
                            value: sweepVals.max,
                            message: sweepErrorMessage
                        }
                    })}
                    debounce={500}
                    onChange={(value) => setValue('sweep', value)}
                    value={sweep}
                    slider={{
                        absMin: sweepVals.absMin,
                        min: sweepVals.min,
                        max: sweepVals.max,
                        absMax: sweepVals.absMax
                    }}
                    error={errors.sweep?.message}
                />
            </div>

            {schedule && (
                <EstimatedRepayments
                    className={styles.loanSummary}
                    schedule={schedule}
                    loading={scheduleLoading}
                />
            )}
            {schedule && (
                <WithdrawalSummary
                    schedule={schedule}
                    loading={scheduleLoading}
                    drawdownAmount={amount}
                />
            )}

            {schedule && (
                <TipBox
                    tip={
                        <FormattedMessage
                            {...messages.minimumRepaymentsNotice}
                            values={{
                                minimumAmount: (
                                    <LoadingCalculationValue>
                                        <MoneyNoDecimals
                                            className={
                                                styles.minimumPaymentsHighlights
                                            }
                                            value={
                                                schedule.data.summary
                                                    .minimum_repayments.amount
                                            }
                                        />
                                    </LoadingCalculationValue>
                                ),
                                minimumRepaymentInterval: (
                                    <span
                                        className={
                                            styles.minimumPaymentsHighlights
                                        }
                                    >
                                        <FormattedMessage
                                            {...messages.repaymentInterval}
                                            values={{
                                                interval:
                                                    schedule.data.summary
                                                        .minimum_repayments
                                                        .interval.amount
                                            }}
                                        />
                                    </span>
                                )
                            }}
                        />
                    }
                />
            )}

            <Button
                className={styles.cta}
                type="submit"
                variant="success"
                disabled={scheduleLoading || Object.keys(errors).length > 0}
                data-testid="start-drawdown"
            >
                <FormattedMessage
                    {...messages.cta}
                    values={{
                        amount: (
                            <MoneyNoDecimals
                                className={styles.summaryAmount}
                                value={amount}
                            />
                        )
                    }}
                />
            </Button>
        </form>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(CustomiseOffer);
